import { Skeleton, Tooltip } from 'antd'
import CantileverButton from 'components/CantileverButton'
import CantileverRow from 'components/CantileverRow'
import {
  CDSTag,
  InspectionHeatmap,
  TextSm,
  WebGLLayer,
} from 'features/assetDatabase'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import { ArrowRight } from 'lucide-react'
import { FC, memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoUnit,
} from 'types'

import { StyledCard } from './cardsStyles'

const StyledRightArrow = styled(ArrowRight)`
  width: 16px;
  height: 16px;
  margin-left: 4px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`

const RawInspectionRow: FC<{
  inspection: GeckoInspection
  component: GeckoComponent
  unit: GeckoUnit
  assetModel?: GeckoAssetModel
  componentDataFrame?: GeckoComponentDataFrame
  onNavigate?: (inspection: GeckoInspection) => void
}> = ({
  inspection,
  component,
  unit,
  assetModel,
  componentDataFrame,
  onNavigate,
}) => {
  const handleNavigate = useCallback(() => {
    onNavigate?.(inspection)
  }, [inspection, onNavigate])
  return (
    <CantileverRow
      key={inspection.id}
      vPadding='0.5rem'
      hPadding='0.5rem'
      leadingContent={
        <>
          <InspectionHeatmap
            unit={unit}
            inspection={inspection}
            component={component}
            assetModel={assetModel}
            componentDataFrame={componentDataFrame}
          />
          <TextSm>{component.data.name}</TextSm>
          <CDSTag inspection={inspection} />
        </>
      }
      trailingContent={
        <ButtonGroup>
          <Tooltip
            title={
              onNavigate ? 'Open in Viewer' : 'No inspection data available'
            }
            open={onNavigate ? false : undefined}>
            <span>
              <CantileverButton
                primary
                disabled={!onNavigate}
                onClick={handleNavigate}>
                Open in Viewer
                <StyledRightArrow />
              </CantileverButton>
            </span>
          </Tooltip>
        </ButtonGroup>
      }
    />
  )
}
const InspectionRow = memo(RawInspectionRow)

export const NiiInspectionsCard: FC = () => {
  const {
    assetModelData: {
      unit,
      inspectionComponentPairs,
      assetModel,
      componentDatasets,
    },
    isLoading,
  } = useNiiReportData()

  const inspectionIdToComponentDataset = useMemo(() => {
    return new Map(
      componentDatasets
        .filter((cds) => cds.tags.inspection)
        .map((cds) => [cds.tags.inspection, cds])
    )
  }, [componentDatasets])

  return isLoading ? (
    <Skeleton active paragraph={{ rows: 3 }} />
  ) : (
    <StyledCard title='Inspections'>
      <WebGLLayer>
        {unit &&
          inspectionComponentPairs.map(({ inspection, component }) => {
            const componentDataFrame = inspectionIdToComponentDataset.get(
              inspection.id
            )
            return (
              <InspectionRow
                key={inspection.id}
                inspection={inspection}
                component={component}
                unit={unit}
                assetModel={assetModel}
                componentDataFrame={componentDataFrame}
                onNavigate={
                  componentDataFrame
                    ? () => {
                        window.open(
                          `/details/${componentDataFrame.tags.component}?cdf=${componentDataFrame.id}`,
                          '_blank'
                        )
                      }
                    : undefined
                }
              />
            )
          })}
      </WebGLLayer>
    </StyledCard>
  )
}
